import { Intersection } from '@api/api/ontology';
import { ThemeSchemaResponse } from '@api/api/theme-overview';
import { Button } from '@design/components/button/Button';
import { Drawer } from '@design/components/drawer/Drawer';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { WebOnlyFeat } from '@terminal/config/WebOnlyFeat';
import { useNavigateToThemeOverview } from '@terminal/hooks/use-navigate-to-theme-overview/useNavigateToThemeOverview';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { queryKeys, queryValues } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { useTranslation } from 'react-i18next';
import { FinancialMacroThemeToCompanies } from './FinancialMacroThemeToCompanies';

export interface IndustryThemeDrawerProps {
  theme: ThemeSchemaResponse;
  intersections?: Intersection[];
}

export const IndustryThemeDrawer = ({
  theme,
  intersections,
}: IndustryThemeDrawerProps) => {
  const { t } = useTranslation(['widget']);
  const { params, deleteQueryParam } = useQueryParams();
  const { navigateToTheme } = useNavigateToThemeOverview();
  const industryThemeId = params[queryKeys.industryThemeId] ?? undefined;
  const isOpen =
    params[queryKeys.activeDrawer] === queryValues.industryThemeDrawer;

  const closeDrawer = () => {
    deleteQueryParam([queryKeys.activeDrawer, queryKeys.industryThemeId]);
  };

  const handleExpand = () => {
    navigateToTheme({
      themeId: industryThemeId,
      source: POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW,
    });
  };

  return (
    <Drawer
      isOpen={isOpen}
      handleCloseClick={closeDrawer}
      headerButton={
        industryThemeId && (
          <WebOnlyFeat>
            <Button
              variant="empty"
              label={t('widget:themeOverview.drawer.themeOverview')}
              iconName="Expand"
              size="xsmall"
              onClick={handleExpand}
            />
          </WebOnlyFeat>
        )
      }
    >
      <ErrorBoundaryWrapper resetKeys={[theme.id]}>
        <FinancialMacroThemeToCompanies
          theme={theme}
          intersections={intersections}
        />
      </ErrorBoundaryWrapper>
    </Drawer>
  );
};
